import { sortAsc } from '../../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, PencilIcon, TrashIcon,
  ServerIcon, GlobeAltIcon, CircleStackIcon,
  CloudIcon, EnvelopeIcon, DevicePhoneMobileIcon,
  ShieldCheckIcon, CubeIcon,
} from '@heroicons/react/24/outline';

const fmt     = n => `Rs. ${(n||0).toLocaleString('en-PK')}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';

const CATEGORIES = ['hosting','domain','database','storage','email','sms','ssl','other'];
const CAT_ICONS = {
  hosting:  ServerIcon,
  domain:   GlobeAltIcon,
  database: CircleStackIcon,
  storage:  CloudIcon,
  email:    EnvelopeIcon,
  sms:      DevicePhoneMobileIcon,
  ssl:      ShieldCheckIcon,
  other:    CubeIcon,
};
const CAT_COLORS = {
  hosting:'#3b6ef8', domain:'#10c98e', database:'#8b5cf6', storage:'#0d9488',
  email:'#f59e0b', sms:'#ef4444', ssl:'#10c98e', other:'#9094a8',
};
const CAT_BG = {
  hosting:'#eef2ff', domain:'#ecfdf5', database:'#f3f0ff', storage:'#f0fdfa',
  email:'#fffbeb', sms:'#fef2f2', ssl:'#ecfdf5', other:'#f0f2f7',
};

const EMPTY_FORM = {
  name:'', category:'hosting', description:'', provider:'',
  monthlyPrice:0, yearlyPrice:0, setupFee:0, currency:'PKR',
  ourMonthlyCost:0, ourYearlyCost:0,
  billingCycle:'monthly', renewalDate:'', isPaid:false,
  isActive:true, isPublic:true, notes:'',
  specs:[],
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sv * { box-sizing:border-box; }
  .sv { font-family:'Sora',sans-serif; color:#1a1d2e; animation:svFade .4s ease both; }
  @keyframes svFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .sv-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .sv-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .sv-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .sv-cats  { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.4rem; }
  .sv-cat-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 14px; border-radius:99px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .sv-cat-btn svg { width:13px; height:13px; stroke-width:2; }
  .sv-cat-btn.active { border-color:var(--cc); background:var(--cb); color:var(--cc); }
  .sv-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:1.1rem; }
  .sv-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.3rem; position:relative; overflow:hidden; transition:transform .15s,box-shadow .15s; animation:svFade .4s ease both; }
  .sv-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.07); }
  .sv-card::after { content:''; position:absolute; top:0; right:0; width:70px; height:70px; border-radius:0 16px 0 70px; opacity:.05; background:var(--cc,#3b6ef8); }
  .sv-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1rem; gap:10px; }
  .sv-icon { width:40px; height:40px; border-radius:11px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .sv-icon svg { width:18px; height:18px; stroke-width:1.8; }
  .sv-card-name { font-size:.92rem; font-weight:700; color:#0f1123; margin-bottom:2px; }
  .sv-card-provider { font-size:.72rem; color:#9094a8; }
  .sv-card-actions { display:flex; gap:5px; }
  .sv-act-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .sv-act-btn svg { width:13px; height:13px; stroke-width:2; }
  .sv-act-btn:hover { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .sv-act-btn.del:hover { background:#fef2f2; border-color:#fecaca; color:#ef4444; }
  .sv-prices { display:grid; grid-template-columns:1fr 1fr; gap:.6rem; margin-bottom:.9rem; }
  .sv-price-box { background:#f8f9fc; border-radius:9px; padding:.6rem .8rem; }
  .sv-price-label { font-size:.6rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sv-price-val   { font-size:.88rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .sv-price-cost  { font-size:.65rem; color:#ef4444; margin-top:2px; font-family:'JetBrains Mono',monospace; }
  .sv-price-profit { font-size:.65rem; color:#10c98e; margin-top:2px; font-family:'JetBrains Mono',monospace; }
  .sv-specs { display:flex; gap:5px; flex-wrap:wrap; margin-bottom:.8rem; }
  .sv-spec { font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; background:#f0f2f7; color:#6b7194; }
  .sv-footer { display:flex; align-items:center; justify-content:space-between; }
  .sv-renewal { font-size:.7rem; color:#9094a8; }
  .sv-pill { font-size:.65rem; font-weight:700; padding:2px 8px; border-radius:99px; }
  .sv-pill.active   { background:#ecfdf5; color:#10c98e; }
  .sv-pill.inactive { background:#fef2f2; color:#ef4444; }
  .sv-pill.paid     { background:#eef2ff; color:#3b6ef8; }
  .sv-pill.unpaid   { background:#fffbeb; color:#f59e0b; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .btn-primary { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-primary svg { width:15px; height:15px; stroke-width:2; }
  /* Modal */
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:600px; max-height:92vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; flex:1; overflow-y:auto; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .fg { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .ff { display:flex; flex-direction:column; gap:5px; margin-bottom:2px; }
  .ff.full { grid-column:1/-1; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus { border-color:#3b6ef8; background:white; }
  .fta { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; resize:vertical; min-height:60px; }
  .section-divider { font-size:.72rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.08em; margin:16px 0 10px; padding-bottom:6px; border-bottom:1px solid #f0f2f8; grid-column:1/-1; }
  .btn-save  { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .spec-row { display:flex; gap:6px; align-items:center; margin-bottom:6px; }
  .spec-row input { flex:1; font-family:'Sora',sans-serif; font-size:.78rem; padding:6px 10px; border:1.5px solid #e0e4f0; border-radius:8px; background:#f8f9fc; outline:none; }
  .spec-row input:focus { border-color:#3b6ef8; background:white; }
  .spec-remove { width:24px; height:24px; border-radius:6px; border:none; background:#fef2f2; color:#ef4444; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:1rem; line-height:1; }
  .add-spec { display:inline-flex; align-items:center; gap:5px; padding:5px 12px; border:1.5px dashed #c7d2fe; border-radius:8px; background:transparent; color:#3b6ef8; font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; cursor:pointer; }
  @media (max-width:768px) { .sv-grid { grid-template-columns:1fr; } .fg { grid-template-columns:1fr; } .ff.full { grid-column:1; } .section-divider { grid-column:1; } }
`;

export default function SuperServices() {
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [catF,     setCatF]     = useState('');
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);

  const load = () => {
    api.get('/super/services')
      .then(r => setServices(r.data||[]))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.type==='checkbox' ? e.target.checked : e.target.value }));

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit   = (s) => {
    setEditing(s);
    setForm({ ...EMPTY_FORM, ...s, renewalDate: s.renewalDate ? new Date(s.renewalDate).toISOString().slice(0,10) : '' });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.category) return toast.error('Name and category required');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/super/services/${editing._id}`, form);
        toast.success('Service updated');
      } else {
        await api.post('/super/services', form);
        toast.success('Service created');
      }
      setModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await api.delete(`/super/services/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const addSpec = () => setForm(p => ({ ...p, specs: [...(p.specs||[]), { label:'', value:'' }] }));
  const setSpec = (i, k, v) => setForm(p => {
    const specs = [...(p.specs||[])];
    specs[i] = { ...specs[i], [k]: v };
    return { ...p, specs };
  });
  const removeSpec = (i) => setForm(p => ({ ...p, specs: p.specs.filter((_,idx) => idx!==i) }));

  const filtered = catF ? services.filter(s => s.category === catF) : services;

  // Summary stats
  const totalCost    = services.reduce((s, sv) => s + (sv.billingCycle==='yearly' ? sv.ourYearlyCost/12 : sv.ourMonthlyCost), 0);
  const totalRevenue = services.reduce((s, sv) => s + (sv.billingCycle==='yearly' ? sv.yearlyPrice/12 : sv.monthlyPrice), 0);

  return (
    <div className="sv"><style>{css}</style>
      <div className="sv-hdr">
        <div>
          <div className="sv-title">Services & Pricing</div>
          <div className="sv-sub">
            Manage all infrastructure services — hosting, domains, databases, storage and more.
            <span style={{marginLeft:10,fontFamily:"'JetBrains Mono',monospace",color:'#ef4444',fontWeight:600}}>
              Monthly cost: {fmt(Math.round(totalCost))}
            </span>
            <span style={{marginLeft:10,fontFamily:"'JetBrains Mono',monospace",color:'#10c98e',fontWeight:600}}>
              Listed revenue: {fmt(Math.round(totalRevenue))}
            </span>
          </div>
        </div>
        <button className="btn-primary" onClick={openCreate}><PlusIcon />Add Service</button>
      </div>

      {/* Category filter */}
      <div className="sv-cats">
        <button className={`sv-cat-btn${!catF?' active':''}`} style={{'--cc':'#3b6ef8','--cb':'#eef2ff'}} onClick={() => setCatF('')}>
          <CubeIcon />All ({services.length})
        </button>
        {sortAsc(CATEGORIES).map(c => {
          const Icon = CAT_ICONS[c];
          const count = services.filter(s => s.category === c).length;
          if (!count) return null;
          return (
            <button key={c} className={`sv-cat-btn${catF===c?' active':''}`}
              style={{'--cc':CAT_COLORS[c],'--cb':CAT_BG[c]}}
              onClick={() => setCatF(catF===c?'':c)}>
              <Icon />{c} ({count})
            </button>
          );
        })}
      </div>

      {loading ? <div className="loading"><div className="spin"/>Loading…</div>
      : filtered.length === 0 ? <div className="empty">No services yet. Add your first service.</div>
      : (
        <div className="sv-grid">
          {filtered.map((s, i) => {
            const Icon   = CAT_ICONS[s.category] || CubeIcon;
            const profit = (s.billingCycle==='yearly' ? s.yearlyPrice/12 : s.monthlyPrice) -
                           (s.billingCycle==='yearly' ? s.ourYearlyCost/12 : s.ourMonthlyCost);
            return (
              <div key={s._id} className="sv-card" style={{'--cc':CAT_COLORS[s.category]||'#3b6ef8', animationDelay:`${i*.04}s`}}>
                <div className="sv-card-top">
                  <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                    <div className="sv-icon" style={{background:CAT_BG[s.category],color:CAT_COLORS[s.category]}}><Icon /></div>
                    <div style={{minWidth:0}}>
                      <div className="sv-card-name">{s.name}</div>
                      <div className="sv-card-provider">{s.provider || s.category}</div>
                    </div>
                  </div>
                  <div className="sv-card-actions">
                    <button className="sv-act-btn" onClick={() => openEdit(s)}><PencilIcon /></button>
                    <button className="sv-act-btn del" onClick={() => handleDelete(s._id)}><TrashIcon /></button>
                  </div>
                </div>

                <div className="sv-prices">
                  <div className="sv-price-box">
                    <div className="sv-price-label">We charge</div>
                    <div className="sv-price-val">
                      {s.monthlyPrice > 0 ? fmt(s.monthlyPrice)+'/mo' : s.yearlyPrice > 0 ? fmt(s.yearlyPrice)+'/yr' : 'Free'}
                    </div>
                    {profit !== 0 && <div className={`sv-price-${profit>=0?'profit':'cost'}`}>{profit>=0?'+':''}{fmt(Math.round(profit))}/mo margin</div>}
                  </div>
                  <div className="sv-price-box">
                    <div className="sv-price-label">Our cost</div>
                    <div className="sv-price-val" style={{color:'#ef4444'}}>
                      {s.ourMonthlyCost > 0 ? fmt(s.ourMonthlyCost)+'/mo' : s.ourYearlyCost > 0 ? fmt(s.ourYearlyCost)+'/yr' : 'Free'}
                    </div>
                    {s.setupFee > 0 && <div style={{fontSize:'.65rem',color:'#9094a8',marginTop:2}}>+{fmt(s.setupFee)} setup</div>}
                  </div>
                </div>

                {(s.specs||[]).length > 0 && (
                  <div className="sv-specs">
                    {s.specs.map((sp,i) => <span key={i} className="sv-spec">{sp.label}: {sp.value}</span>)}
                  </div>
                )}

                <div className="sv-footer">
                  <div className="sv-renewal">
                    {s.renewalDate ? `Renews ${fmtDate(s.renewalDate)}` : s.billingCycle}
                  </div>
                  <div style={{display:'flex',gap:5}}>
                    <span className={`sv-pill ${s.isActive?'active':'inactive'}`}>{s.isActive?'Active':'Inactive'}</span>
                    <span className={`sv-pill ${s.isPaid?'paid':'unpaid'}`}>{s.isPaid?'Paid':'Unpaid'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && createPortal(
        <div className="mb" onClick={() => setModal(false)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{editing ? 'Edit Service' : 'Add Service'}</div>
              <button className="mo-x" onClick={() => setModal(false)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className="fg">
                <div className="ff full"><label className="fl">Service Name *</label><input className="fi" value={form.name} onChange={sf('name')} placeholder="e.g. Hostinger VPS 4GB" /></div>
                <div className="ff">
                  <label className="fl">Category *</label>
                  <select className="fs" value={form.category} onChange={sf('category')}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="ff"><label className="fl">Provider</label><input className="fi" value={form.provider} onChange={sf('provider')} placeholder="e.g. Hostinger, Cloudinary" /></div>
                <div className="ff full"><label className="fl">Description</label><textarea className="fta" value={form.description} onChange={sf('description')} placeholder="What does this service do?" /></div>

                <div className="section-divider">What we charge schools</div>
                <div className="ff"><label className="fl">Monthly Price (Rs.)</label><input type="number" className="fi" value={form.monthlyPrice} onChange={sf('monthlyPrice')} /></div>
                <div className="ff"><label className="fl">Yearly Price (Rs.)</label><input type="number" className="fi" value={form.yearlyPrice} onChange={sf('yearlyPrice')} /></div>
                <div className="ff"><label className="fl">Setup Fee (Rs.)</label><input type="number" className="fi" value={form.setupFee} onChange={sf('setupFee')} /></div>

                <div className="section-divider">Our actual cost</div>
                <div className="ff"><label className="fl">Our Monthly Cost (Rs.)</label><input type="number" className="fi" value={form.ourMonthlyCost} onChange={sf('ourMonthlyCost')} /></div>
                <div className="ff"><label className="fl">Our Yearly Cost (Rs.)</label><input type="number" className="fi" value={form.ourYearlyCost} onChange={sf('ourYearlyCost')} /></div>

                <div className="section-divider">Billing & Status</div>
                <div className="ff">
                  <label className="fl">Billing Cycle</label>
                  <select className="fs" value={form.billingCycle} onChange={sf('billingCycle')}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one_time">One Time</option>
                  </select>
                </div>
                <div className="ff"><label className="fl">Renewal Date</label><input type="date" className="fi" value={form.renewalDate} onChange={sf('renewalDate')} /></div>
                <div className="ff" style={{flexDirection:'row',alignItems:'center',gap:10,paddingTop:8}}>
                  <input type="checkbox" id="isPaid" checked={form.isPaid} onChange={sf('isPaid')} style={{width:16,height:16,cursor:'pointer'}} />
                  <label htmlFor="isPaid" className="fl" style={{margin:0,cursor:'pointer'}}>Marked as Paid</label>
                </div>
                <div className="ff" style={{flexDirection:'row',alignItems:'center',gap:10,paddingTop:8}}>
                  <input type="checkbox" id="isActive" checked={form.isActive} onChange={sf('isActive')} style={{width:16,height:16,cursor:'pointer'}} />
                  <label htmlFor="isActive" className="fl" style={{margin:0,cursor:'pointer'}}>Active</label>
                </div>

                <div className="section-divider">Specs / Features</div>
                <div className="ff full">
                  {(form.specs||[]).map((sp, i) => (
                    <div key={i} className="spec-row">
                      <input placeholder="Label (e.g. Storage)" value={sp.label} onChange={e => setSpec(i,'label',e.target.value)} />
                      <input placeholder="Value (e.g. 100GB)" value={sp.value} onChange={e => setSpec(i,'value',e.target.value)} />
                      <button className="spec-remove" onClick={() => removeSpec(i)}>×</button>
                    </div>
                  ))}
                  <button className="add-spec" onClick={addSpec}><PlusIcon style={{width:12,height:12}}/>Add Spec</button>
                </div>

                <div className="ff full"><label className="fl">Notes</label><textarea className="fta" value={form.notes} onChange={sf('notes')} placeholder="Internal notes…" /></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>{saving?'Saving…':'Save Service'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}